import React, { useState, useCallback, useMemo, useEffect } from 'react';

import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl-next';
import { useDispatch } from 'react-redux';

import Button from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { DiffContent } from '@atlassian/bitkit-diff';

import { ApiCommentOrPlaceholder } from 'src/components/conversation-provider/types';
import { useFlag } from 'src/hooks/flag';
import {
  refreshCodeReviewData,
  WAITING_FOR_APPLIED_SUGGESTION_RELOAD,
} from 'src/redux/pull-request/actions';

import { CodeLine } from './code-line';
import {
  codeSuggestionCardMessages as messages,
  commitResponseFlagMessages,
} from './code-suggestions.i18n';
import { CommitSuggestionModal } from './commit-suggestion-modal';
import { useCannotApplySuggestionReason } from './hooks';
import {
  SuggestionCard,
  SuggestionCardFooter,
  SuggestionCardHeader,
  SuggestionCardHeaderTitle,
} from './styles';
import { Line, CannotApplyReasons } from './types';
import { useCodeSuggestionsAnalytics } from './use-code-suggestions-analytics';
import { usePostCodeSuggestions } from './utils';

interface CodeSuggestionProps {
  diffLine: DiffContent;
  node: any;
  commentId: number;
  apiComment: ApiCommentOrPlaceholder | undefined;
}

function getBtnTooltipText(
  cannotApplyReason: CannotApplyReasons | null
): MessageDescriptor {
  if (cannotApplyReason === null) {
    return messages.applyButtonTooltipTextCanApply;
  } else {
    return (
      messages[cannotApplyReason] ||
      messages.applyButtonTooltipTextCannotApplyFallbackText
    );
  }
}

// function to calculate the width of the gutter based on the max line number
function computeGutterWidth(maxLineNumber: number): number {
  const numDigits =
    maxLineNumber > 0 ? Math.floor(Math.log10(maxLineNumber)) + 1 : 1;
  const baseGutterWidth = 12;
  const extraPadding = (numDigits - 1) * 8;
  return baseGutterWidth + extraPadding;
}

export const CodeSuggestionCard = React.memo(function CodeSuggestionCard({
  diffLine,
  node,
  commentId,
  apiComment,
}: CodeSuggestionProps) {
  // state and fns used for commit suggestion modal
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const dispatch = useDispatch();
  const { showFlag } = useFlag();
  const { formatMessage } = useIntl();

  const {
    publishApplyBtnClickEvt,
    publishSuggestionCommittedEvt,
    publishCancelBtnClickEvt,
    publishCommitBtnClickEvt,
  } = useCodeSuggestionsAnalytics(apiComment);

  const { postCodeSuggestions, loading } = usePostCodeSuggestions();

  function onApplyBtnClick() {
    publishApplyBtnClickEvt();
    openModal();
  }
  const { suggestionIndex = -1, suggestionText: suggestionTextChunk = '' } =
    node.parameters || {};
  const suggestionLines = suggestionTextChunk.split('\n');
  const [maxLineNumber, setMaxLineNumber] = useState<number>(
    diffLine.lineTo || 0
  );
  const [gutterWidth, setGutterWidth] = useState<number>(
    computeGutterWidth(maxLineNumber)
  );

  const diffLineWithoutPrefix = diffLine.content.slice(1);
  const startLineNum = diffLine.lineTo || diffLine.lineFrom || 0;

  const removedLine: Line = useMemo(
    () => ({
      type: 'del',
      content: diffLineWithoutPrefix,
      lineNumber: startLineNum,
    }),
    [startLineNum, diffLineWithoutPrefix]
  );

  const addedLines: Line[] = useMemo(() => {
    return suggestionLines.map((txt: string, idx: number) => ({
      type: 'add',
      content: txt,
      lineNumber: startLineNum + idx,
    }));
  }, [suggestionLines, startLineNum]);

  const computedMaxLineNumber = useMemo(() => {
    return addedLines.length > 0
      ? Math.max(...addedLines.map(line => line.lineNumber))
      : maxLineNumber;
  }, [addedLines, maxLineNumber]);

  useEffect(() => {
    if (computedMaxLineNumber !== maxLineNumber) {
      setMaxLineNumber(computedMaxLineNumber);
      setGutterWidth(computeGutterWidth(computedMaxLineNumber));
    }
  }, [computedMaxLineNumber, maxLineNumber]);

  const cannotApplyReason = useCannotApplySuggestionReason(
    apiComment,
    diffLine,
    suggestionTextChunk
  );
  const suggestionAppliedToComment = apiComment?.inline?.applied_suggestion;
  const isSuggestionApplied = suggestionAppliedToComment === suggestionIndex;

  const applySuggestion = useCallback(
    async (message: string) => {
      // backend accepts an array of suggestions but for now we will just have 1
      const suggestions = [
        {
          comment_id: commentId,
          suggestion_index: suggestionIndex,
          content: suggestionTextChunk,
        },
      ];
      const { success, errorMessage } = await postCodeSuggestions({
        message,
        suggestions,
      });
      if (success) {
        // This makes the comment with code suggestion go away after
        // the commit is submitted successfully. The comment will stay
        // and create an unwanted behavior before reload if we don't add this.
        dispatch(
          refreshCodeReviewData({
            needsDiff: false,
            needsPullRequest: true,
            needsComments: false,
            needsTasks: false,
          })
        );
        // Set WAITING_FOR_APPLIED_SUGGESTION_RELOAD true for autoreload of diff
        dispatch({ type: WAITING_FOR_APPLIED_SUGGESTION_RELOAD.TRUE });
        showFlag({
          autoDismiss: true,
          title: formatMessage(commitResponseFlagMessages.successTitle),
          description: formatMessage(
            commitResponseFlagMessages.successDescription
          ),
          id: 'commit-code-suggestion-success',
          iconType: 'success',
        });
        closeModal();
        publishSuggestionCommittedEvt();
      } else {
        showFlag({
          autoDismiss: true,
          title: formatMessage(commitResponseFlagMessages.errorTitle),
          description:
            errorMessage ||
            formatMessage(commitResponseFlagMessages.errorDefaultDescription),
          id: 'commit-code-suggestion-error',
          iconType: 'error',
        });
      }
    },
    [
      closeModal,
      commentId,
      suggestionTextChunk,
      postCodeSuggestions,
      publishSuggestionCommittedEvt,
      suggestionIndex,
      dispatch,
      showFlag,
      formatMessage,
    ]
  );

  return (
    <>
      <Box
        xcss={SuggestionCard({
          isDisabled:
            suggestionAppliedToComment != null && !isSuggestionApplied,
        })}
      >
        <Box>
          <Box xcss={SuggestionCardHeader}>
            <Box xcss={SuggestionCardHeaderTitle}>
              <FormattedMessage {...messages.suggestionCardTitle} />
            </Box>
            {isSuggestionApplied && (
              <Lozenge appearance="success" isBold>
                <FormattedMessage {...messages.appliedLozenge} />
              </Lozenge>
            )}
          </Box>
        </Box>

        <CodeLine line={removedLine} index={0} gutterWidth={gutterWidth} />
        {addedLines.map((line: Line, index: any) => (
          <CodeLine
            key={index}
            line={line}
            index={index + 1}
            gutterWidth={gutterWidth}
          />
        ))}

        <Box xcss={SuggestionCardFooter}>
          {suggestionAppliedToComment == null && (
            <Tooltip
              content={formatMessage(getBtnTooltipText(cannotApplyReason))}
            >
              <Button
                onClick={onApplyBtnClick}
                aria-haspopup="dialog"
                isDisabled={!!cannotApplyReason}
              >
                <FormattedMessage {...messages.applyButtonText} />
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>
      <ModalTransition>
        {isModalOpen && (
          <CommitSuggestionModal
            loading={loading}
            closeModal={closeModal}
            onConfirm={applySuggestion}
            filePath={apiComment?.inline?.path}
            publishCancelBtnClickEvt={publishCancelBtnClickEvt}
            publishCommitBtnClickEvt={publishCommitBtnClickEvt}
          />
        )}
      </ModalTransition>
    </>
  );
});
